/**
 * A Vorgang without a Stand, as the board and the calculation explain it
 * (round-3 screen check, R3-3). Both used to say something that was not so —
 * the board „Die Auslese hat keinen bearbeitbaren Umriss ergeben" for a
 * Vorgang that was never read, the calculation „erst planen" with a way back
 * to that same board — and both sent the person out of the Vorgang („Zum
 * Start"). Here the cause is the one that holds, and the way on stays in the
 * Vorgang: the reading where there is one, the outline by hand where there
 * is not (E-45), and the record beside it.
 *
 * Pure: the caller reads the reading's status (`useExtraction`, asked only
 * where a drawing is on file — Ruling T7-1) and renders the answer.
 */
import type { ExtractionStatus } from "@/api";

export interface NoStandWay {
  title: string;
  text: string;
  /** The one way on — the reading, or drawing the outline for this Vorgang. */
  next: { label: string; to: string };
}

/** Where the person stands: on the board, or on the calculation. */
export type NoStandScreen = "planning" | "calculation";

const AFTER: Record<NoStandScreen, string> = {
  planning: "dann planst du hier die Gitterroste",
  calculation: "dann planst du die Gitterroste, und hier wird kalkuliert",
};

export function outlineFor(caseId: string): string {
  return `/umriss?case=${encodeURIComponent(caseId)}`;
}

export function recordOf(caseId: string): string {
  return `/vorgang/${encodeURIComponent(caseId)}`;
}

export function noStandWay(caseId: string, reading: ExtractionStatus | null, screen: NoStandScreen): NoStandWay {
  const draw = { label: "Umriss zeichnen →", to: outlineFor(caseId) };
  if (reading === "succeeded") {
    // Ruling T7-1: a reading that is done waits on a person's „Übernehmen"
    // (A-014) — not on a drawing by hand.
    return {
      title: "Die Lesung wartet auf „Übernehmen“.",
      text: "Die Auslese ist fertig, aber noch niemand hat sie übernommen — erst danach hat dieser Vorgang einen Stand.",
      next: { label: "Zur Lesung →", to: `${recordOf(caseId)}/schema` },
    };
  }
  if (reading === "queued" || reading === "running") {
    return {
      title: "Die Auslese läuft noch.",
      text: "Sobald sie fertig ist und du sie übernommen hast, hat dieser Vorgang einen Stand.",
      next: { label: "Zur Auslese →", to: `${recordOf(caseId)}/einlesen` },
    };
  }
  if (reading === "failed") {
    return {
      title: "Noch kein Stand.",
      text: `Die Auslese hat keinen bearbeitbaren Umriss ergeben. Zeichne ihn von Hand, ${AFTER[screen]}.`,
      next: draw,
    };
  }
  return {
    title: "Noch kein Stand.",
    text: `Für diesen Vorgang ist noch kein Umriss gezeichnet. Zeichne ihn zuerst, ${AFTER[screen]}.`,
    next: draw,
  };
}
